import { useEffect, useState } from 'react'
import { api } from '../api.js'
import Filtre, { useRecherche } from '../components/Filtre.jsx'
import ChercherPersonne from '../components/ChercherPersonne.jsx'

// Qui travaille sur les dossiers, et jusqu'où chacun peut aller.
export default function Agents() {
  const [liste, setListe] = useState([])
  const [roles, setRoles] = useState([])
  const [ajout, setAjout] = useState(false)
  const [motDePasse, setMotDePasse] = useState(null)
  const [perimetre, setPerimetre] = useState(null)

  // Chercher quelqu'un ouvre directement son périmètre : c'est ce qu'on vient
  // faire dans neuf cas sur dix quand on cherche un nom ici.
  const setPerimetreDe = async (a) => setPerimetre(await api.agentScope(a.id))
  const [erreur, setErreur] = useState('')
  const [message, setMessage] = useState('')

  const charger = () => { api.agents().then(setListe).catch((e) => setErreur(e.message)) }
  useEffect(() => { charger(); api.roles().then(setRoles) }, [])

  async function changerRole(agent, role) {
    try {
      await api.setAgentRole(agent.id, role)
      setErreur(''); setMessage(`${agent.name} est maintenant ${libelle(roles, role)}.`)
      charger()
    } catch (e) { setErreur(e.message) }
  }

  async function retirer(agent) {
    if (!confirm(`Retirer ${agent.name} de cette collectivité ?`)) return
    try {
      const r = await api.removeAgent(agent.id)
      setErreur(''); setMessage(r.message)
      charger()
    } catch (e) { setErreur(e.message) }
  }

  async function reinitialiser(agent) {
    if (!confirm(`Générer un nouveau mot de passe pour ${agent.name} ?`)) return
    try {
      const r = await api.resetAgentPassword(agent.id)
      setMotDePasse({ nom: agent.name, mdp: r.temporary_password, message: r.message })
    } catch (e) { setErreur(e.message) }
  }

  return (
    <>
      <h1>Agents et droits</h1>
      <p className="chapo">Qui accède aux dossiers de cette collectivité, et jusqu'où chacun peut
        aller. Un même compte peut travailler pour plusieurs collectivités avec un rôle différent
        dans chacune.</p>

      {erreur && <p className="erreur" role="alert">{erreur}</p>}
      {message && <p className="succes" role="status">{message}</p>}

      {liste.length > 5 && (
        <ChercherPersonne gens={liste} placeholder="Chercher une personne dans la collectivité…"
                          onChoisir={(a) => setPerimetreDe(a)} />
      )}

      <button className="btn pri" onClick={() => setAjout(!ajout)}>
        {ajout ? 'Annuler' : 'Ajouter quelqu\'un'}
      </button>

      {ajout && <Formulaire roles={roles} onFini={(r) => {
        setAjout(false); charger()
        if (r.temporary_password) setMotDePasse({ nom: r.name, mdp: r.temporary_password, message: r.message })
        else setMessage(r.message)
      }} onErreur={setErreur} />}

      <table className="tableau">
        <thead><tr><th>Nom</th><th>Adresse électronique</th><th>Rôle</th><th /></tr></thead>
        <tbody>
          {liste.map((a) => (
            <tr key={a.id}>
              <td><strong>{a.name}</strong>{a.moi && <span className="tag">vous</span>}</td>
              <td className="mono">{a.email}</td>
              <td>
                <select value={a.role} onChange={(e) => changerRole(a, e.target.value)}
                        aria-label={`Rôle de ${a.name}`}>
                  {roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                </select>
              </td>
              <td className="actions-cellule">
                <button className="btn sm" onClick={async () => {
                  setPerimetre(await api.agentScope(a.id))
                }}>Ce qu'il voit</button>
                <button className="btn sm" onClick={() => reinitialiser(a)}>Nouveau mot de passe</button>
                {!a.moi && <button className="btn sm dgr" onClick={() => retirer(a)}>Retirer</button>}
              </td>
            </tr>
          ))}
          {!liste.length && <tr><td colSpan={4} className="vide">Aucun agent enregistré.</td></tr>}
        </tbody>
      </table>

      <section className="carte">
        <h2>Deux réglages différents</h2>
        <p className="aide">Le <strong>rôle</strong> dit ce qu'une personne a le droit de faire :
          traiter des dossiers, configurer les démarches, gérer les comptes. Le
          <strong> périmètre</strong> dit ce qu'elle a le droit de voir, et ne dépend pas du rôle.
          Un responsable de l'état civil paramètre toute la plateforme mais ne voit que ses
          quatre démarches, sauf si vous lui ouvrez tout explicitement.</p>
      </section>

      <section className="carte">
        <h2>Ce que permet chaque rôle</h2>
        <dl className="roles-liste">
          {roles.map((r) => (
            <div key={r.key}>
              <dt>{r.label}</dt>
              <dd>{r.description}</dd>
            </div>
          ))}
        </dl>
      </section>

      {perimetre && (
        <Perimetre donnees={perimetre} onFermer={() => setPerimetre(null)}
                   onRafraichir={async () => setPerimetre(await api.agentScope(perimetre.user.id))} />
      )}

      {motDePasse && (
        <div className="fenetre" role="dialog" aria-label="Mot de passe provisoire">
          <div className="fenetre-corps">
            <h2>Mot de passe pour {motDePasse.nom}</h2>
            <p className="mdp-provisoire">{motDePasse.mdp}</p>
            <p className="aide">{motDePasse.message}</p>
            <p className="avertissement">Notez-le maintenant : il ne sera plus affiché.</p>
            <div className="ligne separee">
              <div className="pousse" />
              <button className="btn" onClick={() => {
                navigator.clipboard?.writeText(motDePasse.mdp)
              }}>Copier</button>
              <button className="btn pri" onClick={() => setMotDePasse(null)}>J'ai noté</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function Formulaire({ roles, onFini, onErreur }) {
  const [nom, setNom] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('agent')

  async function ajouter(e) {
    e.preventDefault()
    try {
      onFini(await api.addAgent({ name: nom, email, role }))
    } catch (err) { onErreur(err.message) }
  }

  const choisi = roles.find((r) => r.key === role)

  return (
    <form className="carte" onSubmit={ajouter}>
      <h2>Ajouter quelqu'un</h2>
      <div className="ligne-champs">
        <div className="champ">
          <label htmlFor="a-nom">Nom et prénom</label>
          <input id="a-nom" value={nom} onChange={(e) => setNom(e.target.value)} required />
        </div>
        <div className="champ">
          <label htmlFor="a-mail">Adresse électronique</label>
          <input id="a-mail" type="email" value={email}
                 onChange={(e) => setEmail(e.target.value)} required />
        </div>
      </div>
      <div className="champ">
        <label htmlFor="a-role">Rôle</label>
        <select id="a-role" value={role} onChange={(e) => setRole(e.target.value)}>
          {roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>
        {choisi && <p className="aide">{choisi.description}</p>}
      </div>
      <p className="aide">Un mot de passe provisoire sera généré. Si cette personne a déjà un
        compte UCIVIS, elle garde le sien.</p>
      <button className="btn pri">Ajouter</button>
    </form>
  )
}

// Ce qu'une personne voit : des services entiers, des démarches précises, ou tout.
function Perimetre({ donnees, onFermer, onRafraichir }) {
  const [message, setMessage] = useState('')
  const [q, setQ] = useState('')
  const services = useRecherche(donnees.services, q, CHAMPS_SERVICE)
  const formulaires = useRecherche(donnees.forms, q, CHAMPS_FORM)

  async function basculer(payload) {
    const r = await api.setAgentScope(donnees.user.id, payload)
    if (r.message) setMessage(r.message)
    onRafraichir()
  }

  const parCategorie = formulaires.reduce((acc, f) => {
    (acc[f.category] ||= []).push(f)
    return acc
  }, {})

  return (
    <div className="fenetre" role="dialog" aria-label={`Périmètre de ${donnees.user.name}`}>
      <div className="fenetre-corps large">
        <h2>Ce que voit {donnees.user.name}</h2>
        {message && <p className="succes" role="status">{message}</p>}

        <label className="case grande">
          <input type="checkbox" checked={donnees.scope_all}
                 onChange={(e) => basculer({ scope_all: e.target.checked })} />
          <span>
            <strong>Tous les dossiers de la collectivité</strong>
            <span className="aide">À réserver au secrétariat de mairie ou à la direction
              générale. Les autres ne voient que ce qui suit.</span>
          </span>
        </label>

        {!donnees.scope_all && (
          <>
            {(donnees.services.length + donnees.forms.length) > 10 && (
              <Filtre valeur={q} onChange={setQ}
                      placeholder="Chercher un service ou une démarche…"
                      nb={services.length + formulaires.length}
                      total={donnees.services.length + donnees.forms.length} />
            )}

            <h3>Par service</h3>
            <p className="aide">La personne voit tous les dossiers confiés à ces services.</p>
            <ul className="equipe">
              {services.map((s) => (
                <li key={s.key}>
                  <label className="case">
                    <input type="checkbox" checked={s.dedans}
                           onChange={(e) => basculer({ service_key: s.key, dedans: e.target.checked })} />
                    <span>{s.name}</span>
                  </label>
                </li>
              ))}
              {!services.length && (
                <li className="aide">{q ? 'Aucun service ne correspond.' : 'Aucun service créé.'}</li>
              )}
            </ul>

            <h3>Démarche par démarche</h3>
            <p className="aide">Pour ouvrir quelques démarches sans donner le service entier.</p>
            {Object.entries(parCategorie).map(([cat, formulaires]) => (
              <div key={cat}>
                <h4>{cat}</h4>
                <ul className="equipe">
                  {formulaires.map((f) => (
                    <li key={f.key}>
                      <label className="case">
                        <input type="checkbox" checked={f.dedans || f.ouverte}
                               disabled={f.ouverte}
                               onChange={(e) => basculer({ form_key: f.key, dedans: e.target.checked })} />
                        <span>
                          {f.title}
                          {f.ouverte && <span className="tag">ouverte à toute la mairie</span>}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </>
        )}

        <div className="ligne separee">
          <div className="pousse" />
          <button className="btn pri" onClick={onFermer}>Terminé</button>
        </div>
      </div>
    </div>
  )
}

const libelle = (roles, key) => roles.find((r) => r.key === key)?.label || key
const CHAMPS_SERVICE = ['name']
const CHAMPS_FORM = ['title', 'category']
